import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@wovendays/i18n';
import { useAuth } from '@/contexts/auth';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate(): boolean {
    const newErrors: { email?: string } = {};

    if (!email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await login(email);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t('login.failed')
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="grid gap-2">
          {/* Hero tile */}
          <Tile variant="dark" className="p-8">
            <p className="font-mono text-xs uppercase tracking-widest text-primary-400 mb-3">{t('common:appName')}</p>
            <h1 className="font-serif text-3xl italic text-white">{t('tagline')}</h1>
          </Tile>

          {/* Form tile */}
          <Tile variant="cream" className="p-6">
            {submitError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-5">
                <label htmlFor="email" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                  {t('email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-1.5 text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-2xl bg-primary-600 px-6 py-3 font-mono text-sm uppercase tracking-wide text-white transition-all hover:bg-primary-700 hover:-translate-y-px hover:shadow-elevated-hover active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isSubmitting ? t('login.authenticating') : t('login.signIn')}
              </button>
            </form>
          </Tile>

          {/* Bottom row */}
          <div className={`grid gap-2 ${import.meta.env.DEV ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <Link to="/register">
              <Tile variant="cream" interactive className="p-4 text-center h-full flex items-center justify-center">
                <span className="font-mono text-xs uppercase tracking-wide text-primary-600">{t('login.createAccount')}</span>
              </Tile>
            </Link>
            <Tile variant="cream" className="p-4 text-center flex items-center justify-center">
              <span className="font-mono text-xs uppercase tracking-wide text-muted-foreground">{t('login.forgotPassword')}</span>
            </Tile>
            {import.meta.env.DEV && (
              <Link to="/dev/login">
                <Tile variant="cream" interactive className="p-4 text-center h-full flex items-center justify-center">
                  <span className="font-mono text-xs uppercase tracking-wide text-muted-foreground">{t('devLogin.title')}</span>
                </Tile>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
